import {
  AdapterIODirection,
  type AdapterIOFailureReason,
  type AdapterIoResultListApiData,
  type AdapterReviewCaseListApiData,
  type BookingStatus
} from '@navio/nsdk';
import { filterUnitFor, useFilterUnits } from 'noddi-ui-web';
import {
  ADAPTER_IO_DIRECTION_OPTIONS,
  ADAPTER_IO_FAILURE_REASON_OPTIONS,
  ADAPTER_IO_STATUS_OPTIONS,
  ADAPTER_REVIEW_SEVERITY_OPTIONS,
  ADAPTER_TYPE_OPTIONS,
  type AdapterIOEntityTypeValue
} from 'noddi-util';
import { adapterLinkedFilterUnit, useAdapterLinkedDomainFilters } from './useAdapterLinkedDomainFilters';

type ListOnly = 'pageIndex' | 'pageSize' | 'ordering' | 'search';

const adapterIoResultFilterUnit = filterUnitFor<Omit<NonNullable<AdapterIoResultListApiData['query']>, ListOnly>>();
const adapterReviewCaseFilterUnit = filterUnitFor<Omit<NonNullable<AdapterReviewCaseListApiData['query']>, ListOnly>>();

const FAILURE_REASON_DESCRIPTION = 'Filter by structured failure/skip reason (missing phone, hydrate failed, …)';

// Tray and sidebar both write `directions`.
const directionFilter = adapterLinkedFilterUnit.multiSelect('directions', {
  title: 'Direction',
  description: 'Filter by push (outbound) or pull (inbound)',
  options: ADAPTER_IO_DIRECTION_OPTIONS,
  exclude: true,
  quickFilter: {
    label: 'Direction',
    options: [
      { value: 'all', label: 'All' },
      { value: 'push', label: 'Push', values: [AdapterIODirection.PUSH] },
      { value: 'pull', label: 'Pull', values: [AdapterIODirection.PULL] }
    ]
  }
});

const RESULT_FILTERS = [
  directionFilter,
  adapterIoResultFilterUnit.multiSelect('statuses', {
    title: 'Status',
    description: 'Filter by adapter I/O step status',
    options: ADAPTER_IO_STATUS_OPTIONS,
    exclude: true
  }),
  adapterIoResultFilterUnit.multiSelect('failureReasons', {
    title: 'Failure reason',
    description: FAILURE_REASON_DESCRIPTION,
    options: ADAPTER_IO_FAILURE_REASON_OPTIONS,
    exclude: true
  }),
  adapterIoResultFilterUnit.multiSelect('adapterTypes', {
    title: 'Adapter',
    description: 'Filter by target external system',
    options: ADAPTER_TYPE_OPTIONS,
    exclude: true
  }),
  adapterIoResultFilterUnit.numberRange('attempts', {
    title: 'Attempts',
    description: 'Filter by number of attempts on the preferred step',
    minLabel: 'Min attempts',
    maxLabel: 'Max attempts',
    exclude: true
  }),
  adapterIoResultFilterUnit.dateRange('lastAttemptedAt', {
    title: 'Last attempted',
    description: 'Filter by when the preferred step was last attempted',
    exclude: true
  }),
  adapterIoResultFilterUnit.dateRange('resolvedAt', {
    title: 'Resolved at',
    description: 'Filter by when a human marked the step resolved',
    exclude: true
  }),
  adapterIoResultFilterUnit.boolean('unresolved', {
    title: 'Unresolved only',
    description: 'Show only results that have not been marked resolved'
  })
] as const;

/** The review queue's failure reasons on their own URL keys, so the I/O results view's pick does not carry over. */
const reviewFailureReasons = filterUnitFor<{
  reviewFailureReasons?: AdapterIOFailureReason[];
  excludedReviewFailureReasons?: AdapterIOFailureReason[];
}>().multiSelect('reviewFailureReasons', {
  title: 'Failure',
  description: FAILURE_REASON_DESCRIPTION,
  options: ADAPTER_IO_FAILURE_REASON_OPTIONS,
  exclude: true
});

const REVIEW_FILTERS = [
  directionFilter,
  adapterReviewCaseFilterUnit.multiSelect('severity', {
    title: 'Severity',
    description: 'Filter by review-case severity',
    options: ADAPTER_REVIEW_SEVERITY_OPTIONS,
    exclude: true
  }),
  adapterReviewCaseFilterUnit.map([reviewFailureReasons], (params) => ({
    failureReasons: params.reviewFailureReasons,
    excludedFailureReasons: params.excludedReviewFailureReasons
  }))
] as const;

/** One owner for the URL state and async options shared by both adapter table modes. */
export const useAdapterIOTableFilters = ({
  entityType,
  serviceDepartmentIds,
  baseExcludedStatusIds
}: {
  /** The selected entity tab; it narrows both lists, so there is no entity type control. */
  entityType?: AdapterIOEntityTypeValue;
  serviceDepartmentIds?: number[];
  /** Booking statuses the I/O results view leaves out on top of the status filter's own. */
  baseExcludedStatusIds?: BookingStatus[];
} = {}) => {
  const domainFilters = useAdapterLinkedDomainFilters({ serviceDepartmentIds, baseExcludedStatusIds });
  const entityTypes = entityType ? [entityType] : undefined;

  return {
    resultFilters: useFilterUnits([
      adapterIoResultFilterUnit.locked({ entityTypes }),
      ...RESULT_FILTERS,
      domainFilters
    ]),
    reviewFilters: useFilterUnits([
      // The review queue holds open cases only.
      adapterReviewCaseFilterUnit.locked({ entityTypes, unresolved: true }),
      ...REVIEW_FILTERS,
      domainFilters
    ])
  };
};
